import { useEffect, useState } from 'react';
import { useQuery } from 'react-apollo';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { fg } from '@confluence/feature-gating';
import { useSessionData, useIsLicensed } from '@confluence/session-data';

import { UserPersonalizationQuery } from '../useOnboardingEligible/UserPersonalizationQuery.experimentalgraphql';
import { CONTEXTUAL_INVITE_NUDGE } from '../../constants/onboarding-state/onboarding-contextual-invite-nudge';
import { EDITOR_ONBOARDING_STATE_KEYS } from '../../constants/onboarding-state/onboarding-editor-constants';
import {
	useGetOnboardingState,
	useSetOnboardingState,
	deserializeState,
} from '../onboardingState/onboardingState';
import { useIsXflowUser } from '../useIsXflowUser';
import { useIsEstablishedUserTraitName } from '../useIsEstablishedUserTraitName';
import { memoizedGetInvitePermissions } from '../../utils/getInvitePermissions';

const CONFLUENCE_KEY = 'confluence';

const LIVE_PAGE_NUDGE = {
	LIVE_PAGE_CONTEXTUAL_NUDGE_SEEN: 'live-page-nudge:wasLivePageNudgeSeen',
};

/**
 * Determines eligibility for the Contextual Invite Nudge experiment
 * @returns {object} - an object containing:
 * @returns {boolean} contextualInviteNudgeEligibleTag- Determines whether the user is eligible for the contextual invite nudge
 * @returns {object} onboardingState - Relevant onboarding state data
 */
export const useContextualInviteNudge = (): {
	contextualInviteNudgeEligibleTag: boolean;
	onboardingState: any;
} => {
	const [isEligibleTag, setIsEligibleTag] = useState(false);

	// Contexts and hooks
	const { userId, cloudId } = useSessionData();
	const isLicensed = useIsLicensed();

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { setOnboardingState } = useSetOnboardingState();

	const isEstablishedUserTraitName = useIsEstablishedUserTraitName();
	// Onboarding state
	const {
		data: onboardingStateData,
		loading: onboardingStateLoading,
		error: onboardingStateError,
	} = useGetOnboardingState(
		[
			CONTEXTUAL_INVITE_NUDGE.EXPERIMENT_ELIGIBLE_TAG,
			CONTEXTUAL_INVITE_NUDGE.WAS_CONFETTI_SEEN,
			EDITOR_ONBOARDING_STATE_KEYS.HAS_USER_PUBLISHED,
			LIVE_PAGE_NUDGE.LIVE_PAGE_CONTEXTUAL_NUDGE_SEEN,
		],
		!fg('cc_onboarding_experience'),
	);

	const onboardingState = deserializeState(onboardingStateData);
	const { contextualInviteNudgeEligibleTag, hasUserPublished } = onboardingState;

	const skip =
		!isLicensed ||
		onboardingStateLoading ||
		Boolean(contextualInviteNudgeEligibleTag) ||
		Boolean(hasUserPublished) ||
		!fg('cc_onboarding_experience');

	// User personalization query for isEstablishedUser
	const {
		data: userPersonalizationQueryData,
		loading: userPersonalizationQueryLoading,
		error: userPersonalizationQueryError,
	} = useQuery(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		UserPersonalizationQuery,
		{
			variables: {
				userId: userId!,
			},
			skip,
		},
	);

	const attributes: Record<string, string>[] =
		userPersonalizationQueryData?.experimentalUserPersonalization?.attributes ?? [];
	const isEstablishedUser =
		attributes.find((attr: Record<string, string>) => attr?.name === isEstablishedUserTraitName)
			?.value === 'true';

	// Xflow user check
	const {
		isXflowUser,
		isLoading: xFlowLoading,
		error: xFlowError,
	} = useIsXflowUser({
		skip,
	});

	// Combined loading and error states
	const loading = userPersonalizationQueryLoading || onboardingStateLoading || xFlowLoading;
	const error = userPersonalizationQueryError || onboardingStateError || xFlowError;
	useEffect(() => {
		if (skip || loading || error || isEstablishedUser) {
			return;
		}

		const fetchInvitePermsAndSetEligibility = async () => {
			const response = await memoizedGetInvitePermissions(cloudId);
			if (response instanceof Error) {
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						source: 'editorOrViewPage',
						action: 'failed',
						actionSubject: 'getInvitePermissions',
						attributes: {
							message: response?.message,
							isXflowUser,
						},
					},
				}).fire();
			}

			const canInviteToConfluence: boolean = [
				...(response?.directInvite ?? []),
				...(response?.invitePendingApproval ?? []),
			].some((item) => item.includes(CONFLUENCE_KEY));

			if (canInviteToConfluence) {
				void setOnboardingState({
					key: CONTEXTUAL_INVITE_NUDGE.EXPERIMENT_ELIGIBLE_TAG,
					value: 'true',
				});
				setIsEligibleTag(true);
			}
		};
		void fetchInvitePermsAndSetEligibility();
	}, [
		error,
		loading,
		skip,
		cloudId,
		contextualInviteNudgeEligibleTag,
		isEstablishedUser,
		isXflowUser,
		setOnboardingState,
		createAnalyticsEvent,
	]);

	return {
		contextualInviteNudgeEligibleTag: Boolean(isEligibleTag || contextualInviteNudgeEligibleTag),
		onboardingState,
	};
};
